import { useState } from "react";
import { cn } from "@/lib/utils";
import { useLocale } from "@/hooks/useLocale";
import { fmtDateTime } from "@/lib/locale-format";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  BookText, PenLine, Copy, Check, Download, FileText, FileJson,
  X, ChevronDown, MoreVertical,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { bidiComponents } from "@/lib/bidi-components";
import type { JournalEntry } from "./JournalPanel";

// ── Utilities ──

export function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

// formatDate is locale-aware; called inside EntryCard with locale from useLocale()
function formatDate(iso: string, locale: string): string {
  try {
    return fmtDateTime(iso, locale, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Entry Card ──

export function EntryCard({
  entry, index, isExpanded, onToggle, onCopy, isCopied, onDownloadMd,
}: {
  entry: JournalEntry;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onCopy: () => void;
  isCopied: boolean;
  onDownloadMd: () => void;
}) {
  const { locale } = useLocale();
  const isStory = entry.type === "story";
  const Icon = isStory ? BookText : PenLine;
  const label = isStory ? `Chapter ${index}` : `Entry ${index}`;
  const turnRange = isStory && entry.fromTurn != null && entry.toTurn != null
    ? `T${entry.fromTurn}–${entry.toTurn}`
    : null;

  return (
    <div className={cn(
      "rounded-lg border transition-colors",
      isStory ? "border-primary/20 bg-primary/5" : "border-border bg-card/50",
    )}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/20 transition-colors rounded-t-lg group/entry"
      >
        <Icon className={cn("w-3.5 h-3.5 shrink-0", isStory ? "text-primary" : "text-muted-foreground")} />
        <span className="text-xs font-medium text-foreground flex-1 min-w-0 truncate">
          {label}
          {turnRange && <span className="text-muted-foreground ml-1.5 font-normal">({turnRange})</span>}
        </span>
        <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{entry.wordCount}w</span>
        <ChevronDown className={cn("w-3 h-3 text-muted-foreground shrink-0 transition-transform", isExpanded && "rotate-180")} />
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-border/40">
          <ScrollArea className="max-h-[400px] pt-2">
            <div className="prose prose-sm dark:prose-invert max-w-none text-xs leading-relaxed" dir="auto">
              <ReactMarkdown components={bidiComponents}>{entry.content}</ReactMarkdown>
            </div>
          </ScrollArea>
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] text-muted-foreground">{formatDate(entry.createdAt, locale)}</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 px-1.5" onClick={(e) => { e.stopPropagation(); onCopy(); }}>
                {isCopied ? <Check className="w-3 h-3 text-primary" /> : <Copy className="w-3 h-3" />}
                {isCopied ? "Copied" : "Copy"}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={(e) => e.stopPropagation()}>
                    <MoreVertical className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[120px]">
                  <DropdownMenuItem className="text-xs gap-2" onClick={() => onDownloadMd()}>
                    <Download className="w-3 h-3" /> Download .md
                  </DropdownMenuItem>
                  {entry.debug && (
                    <DropdownMenuItem className="text-xs gap-2" onClick={() => {
                      downloadFile(JSON.stringify(entry.debug, null, 2), `${entry.type}-${index}-debug.json`, "application/json");
                    }}>
                      <FileJson className="w-3 h-3" /> Debug .json
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Entry Collection Reader (full-screen overlay) ──

export function EntryCollectionReader({
  entries, title, filenameBase, onClose,
}: {
  entries: JournalEntry[];
  title: string;
  filenameBase: string;
  onClose: () => void;
}) {
  const { t } = useLocale();
  const fullContent = entries
    .map((entry, index) => `## ${entry.type === "story" ? `Chapter ${index + 1}` : `Entry ${index + 1}`}\n\n${entry.content}`)
    .join("\n\n***\n\n");
  const totalWords = fullContent.split(/\s+/).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <BookText className="w-5 h-5 text-primary" />
          <div>
            <h2 className="text-sm font-semibold">{title}</h2>
            <p className="text-[10px] text-muted-foreground">{entries.length} entries · {totalWords} words</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={() => {
            downloadFile(`# ${title}\n\n${fullContent}`, `${filenameBase}.md`, "text/markdown");
            toast.success(t.play_downloaded);
          }}>
            <FileText className="w-3 h-3" /> .md
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="max-w-2xl mx-auto px-6 py-8">
          <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed" dir="auto">
            <ReactMarkdown components={bidiComponents}>{fullContent}</ReactMarkdown>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

// ── Story Mode Dialog ──

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, RefreshCw, RotateCcw } from "lucide-react";

export type StoryMode = "continue" | "rewrite_chapter" | "rewrite_all";

export function StoryModeDialog({
  open, onSelect, onCancel, chapters,
}: {
  open: boolean;
  onSelect: (mode: StoryMode, chapterIndex?: number) => void;
  onCancel: () => void;
  chapters: JournalEntry[];
}) {
  const [selectedChapter, setSelectedChapter] = useState(chapters.length - 1);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <BookText className="w-4 h-4 text-primary" /> Story Options
          </DialogTitle>
          <DialogDescription className="text-xs">
            {chapters.length} chapter{chapters.length !== 1 ? "s" : ""} written
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Button variant="outline" size="sm" className="w-full justify-start gap-2 text-xs h-10" onClick={() => onSelect("continue")}>
            <Plus className="w-3.5 h-3.5 text-primary" />
            <div className="text-left">
              <div className="font-medium">Write New Chapter</div>
              <div className="text-[10px] text-muted-foreground">Continue from where the story left off</div>
            </div>
          </Button>

          <Button variant="outline" size="sm" className="w-full justify-start gap-2 text-xs h-10" onClick={() => onSelect("rewrite_chapter", selectedChapter)}>
            <RefreshCw className="w-3.5 h-3.5 text-amber-500" />
            <div className="text-left flex-1">
              <div className="font-medium">Rewrite Chapter</div>
              <div className="text-[10px] text-muted-foreground">Regenerate a specific chapter</div>
            </div>
          </Button>

          {chapters.length > 1 && (
            <div className="pl-8 pr-2">
              <select
                className="w-full text-xs bg-muted/50 border border-border rounded px-2 py-1.5"
                value={selectedChapter}
                onChange={(e) => setSelectedChapter(Number(e.target.value))}
              >
                {chapters.map((ch, i) => (
                  <option key={ch.id} value={i}>
                    Ch. {i + 1} — T{ch.fromTurn || "?"}–{ch.toTurn || "?"} ({ch.wordCount}w)
                  </option>
                ))}
              </select>
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2 text-xs h-10 border-destructive/30 hover:border-destructive/50"
            onClick={() => onSelect("rewrite_all")}
          >
            <RotateCcw className="w-3.5 h-3.5 text-destructive" />
            <div className="text-left">
              <div className="font-medium">Rewrite Everything</div>
              <div className="text-[10px] text-muted-foreground">AI re-reads all turns, rewrites from scratch</div>
            </div>
          </Button>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}